import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TabStrip } from '../components/TabStrip.js';
import type { AdminHttpClient } from '../http.js';
import type { ToastMessage } from '../types.js';
import { PlatformScreen } from './Platform.js';
import { RatesScreen } from './Rates.js';
import { PartnerCreditsTab } from './platform/PartnerCreditsTab.js';
import { TenantCreditsTab } from './platform/TenantCreditsTab.js';
import { PlatformAdminsTab } from './platform/PlatformAdminsTab.js';
import { PlatformAnnouncementsTab } from './platform/PlatformAnnouncementsTab.js';
import { DiagnosticsTab } from './platform/DiagnosticsTab.js';
import { BillingManagementTab } from './platform/BillingManagementTab.js';

// Partner management moved to the Partner console (`PartnersScreen`); this
// console is platform-scoped operations only.
const TABS = [
  { id: 'tenants', label: 'platformConsole.tabTenants' },
  { id: 'modelSettings', label: 'platformConsole.tabModelSettings' },
  { id: 'accessSettings', label: 'platformConsole.tabAccessSettings' },
  { id: 'rates', label: 'platformConsole.tabRates' },
  { id: 'partnerCredits', label: 'platformConsole.tabPartnerCredits' },
  { id: 'tenantCredits', label: 'platformConsole.tabTenantCredits' },
  { id: 'billing', label: 'platformConsole.tabBilling' },
  { id: 'platformAdmins', label: 'platformConsole.tabPlatformAdmins' },
  { id: 'announcements', label: 'platformConsole.tabAnnouncements' },
  { id: 'diagnostics', label: 'platformConsole.tabDiagnostics' },
] as const;
type TabId = (typeof TABS)[number]['id'];
type PlatformTabScope = 'tenants' | 'modelSettings' | 'accessSettings';

function platformTabScopeFor(tab: TabId): PlatformTabScope | undefined {
  switch (tab) {
    case 'tenants':
    case 'modelSettings':
    case 'accessSettings':
      return tab;
    default:
      return undefined;
  }
}

export function PlatformConsole({
  http,
  tenantHttp,
  onToast,
  selectedTenantId,
  onTenantContext,
}: {
  http: AdminHttpClient;
  tenantHttp?: AdminHttpClient;
  onToast: (toast: ToastMessage) => void;
  selectedTenantId?: string;
  onTenantContext: (tenantId: string) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabId>('tenants');
  const platformTabScope = platformTabScopeFor(tab);

  return (
    <section className="content-stack">
      <div className="wide-navigation">
      <TabStrip
        tabs={TABS.map((item) => ({ id: item.id, label: t(item.label) }))}
        value={tab}
        onChange={setTab}
      />
      </div>
      <label className="compact-navigation">
        {t('nav.Platform Console')}
        <select value={tab} onChange={(event) => setTab(event.target.value as TabId)}>
          {TABS.map((item) => <option key={item.id} value={item.id}>{t(item.label)}</option>)}
        </select>
      </label>
      <div hidden={tab !== 'tenants' && tab !== 'modelSettings' && tab !== 'accessSettings'}>
        <PlatformScreen
          http={http}
          {...(tenantHttp ? { tenantHttp } : {})}
          onToast={onToast}
          {...(selectedTenantId !== undefined ? { selectedTenantId } : {})}
          onTenantContext={onTenantContext}
          {...(platformTabScope ? { tabScope: platformTabScope } : {})}
        />
      </div>
      <div hidden={tab !== 'rates'}>
        <RatesScreen http={http} onToast={onToast} onOpenModels={() => setTab('modelSettings')} />
      </div>
      <div hidden={tab !== 'partnerCredits'}>
        <PartnerCreditsTab http={http} onToast={onToast} />
      </div>
      <div hidden={tab !== 'tenantCredits'}>
        <TenantCreditsTab http={http} onToast={onToast} />
      </div>
      <div hidden={tab !== 'billing'}>
        <BillingManagementTab http={http} onToast={onToast} active={tab === 'billing'} />
      </div>
      <div hidden={tab !== 'platformAdmins'}>
        <PlatformAdminsTab http={http} onToast={onToast} />
      </div>
      <div hidden={tab !== 'announcements'}>
        <PlatformAnnouncementsTab http={http} onToast={onToast} active={tab === 'announcements'} />
      </div>
      <div hidden={tab !== 'diagnostics'}>
        <DiagnosticsTab http={http} onToast={onToast} active={tab === 'diagnostics'} />
      </div>
    </section>
  );
}
